import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Calendar, Save, LogOut, Users, Eye, RefreshCw } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { getClasses } from '../data'
import { Schedule, Enrollment, apiService } from '../core'
import { useAuth } from '../contexts/AuthContext'
import { 
  isTodayOrFuture, 
  getMinDateIST, 
  compareISTDates,
  isScheduleInFuture,
  compareTimesIST,
  getMinTimeIST,
  isValidScheduleTime,
  formatTimeRangeWithAMPM,
  formatReadableDateParts
} from '../core'

const scheduleSchema = z.object({
  classId: z.number().min(1, 'Please select a class'),
  specificDate: z.string()
    .min(1, 'Please select a date')
    .refine((date) => isTodayOrFuture(date), 'Date must be today or in the future'),
  startTime: z.string().min(1, 'Please enter start time'),
  endTime: z.string().min(1, 'Please enter end time'),
  isDemo: z.boolean(),
  maxCapacity: z.number().min(1, 'Capacity must be at least 1'),
}).refine((data) => {
  // Validate start time for today
  if (data.specificDate === getMinDateIST()) {
    return isValidScheduleTime(data.specificDate, data.startTime)
  }
  return true
}, {
  message: 'Start time must be at least 1 hour in the future for today',
  path: ['startTime']
}).refine((data) => {
  // Validate end time is after start time
  return compareTimesIST(data.endTime, data.startTime) > 0
}, {
  message: 'End time must be after start time',
  path: ['endTime']
})

type ScheduleForm = z.infer<typeof scheduleSchema>

export default function Admin() {
  const { isAuthenticated, logout, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'schedules' | 'enrollments'>('schedules')
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [enrollments, setEnrollments] = useState<Enrollment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean
    type: 'status' | 'delete' | 'edit' | null
    enrollment?: Enrollment | null
    schedule?: Schedule | null
    newStatus?: string
  }>({
    isOpen: false,
    type: null,
    enrollment: null,
    schedule: null
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [showAllData, setShowAllData] = useState(false)

  const classes = getClasses()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<ScheduleForm>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      isDemo: false,
      maxCapacity: 15
    }
  })

  // Removed watchedScheduleType as we only support specific dates now

  useEffect(() => {
    // Wait for authentication to finish loading
    if (authLoading) {
      return
    }
    
    if (!isAuthenticated) {
      navigate('/admin-login')
      return
    }
    
    loadSchedules()
    loadEnrollments() // Load enrollments on initial load for the info card
  }, [isAuthenticated, authLoading, navigate])



  // Only load data when filter changes (tab switching and page return handled by manual refresh)
  useEffect(() => {
    // Reload data when filter toggle changes
    loadSchedules()
    if (activeTab === 'enrollments') {
      loadEnrollments()
    }
  }, [showAllData])


  const loadSchedules = async (forceRefresh: boolean = false) => {
    try {
      setIsLoading(true)
      const data = await apiService.getSchedules(undefined, forceRefresh)
      
      let filteredSchedules = data
      
      if (!showAllData) {
        // Filter to show only future schedules considering both date and time in IST
        filteredSchedules = data.filter(schedule => 
          isScheduleInFuture(schedule.specificDate, schedule.startTime)
        )
      }
      
      // Sort by date (IST), then by start time
      const sortedSchedules = filteredSchedules.sort((a, b) => {
        const dateComparison = compareISTDates(a.specificDate, b.specificDate)
        if (dateComparison === 0) {
          return compareTimesIST(a.startTime, b.startTime)
        }
        return dateComparison
      })
      
      setSchedules(sortedSchedules)
    } catch (error) {
      // Handle error silently
      toast.error('Failed to load schedules')
    } finally {
      setIsLoading(false)
    }
  }

  const refreshCurrentTab = async () => {
    setIsRefreshing(true)
    try {
      // Always refresh both schedules and enrollments for complete data sync
      await loadSchedules(true) // Force refresh schedules
      await loadEnrollments(true) // Force refresh enrollments
      toast.success('All data refreshed successfully')
    } catch (error) {
      toast.error('Failed to refresh data')
    } finally {
      setIsRefreshing(false)
    }
  }

  const loadEnrollments = async (forceRefresh: boolean = false) => {
    try {
      setIsLoading(true)
      const result = await apiService.getEnrollments(undefined, forceRefresh)
      
      let filteredEnrollments = result || []
      
      if (!showAllData) {
        // Filter enrollments to show only those for future schedules considering both date and time in IST
        filteredEnrollments = (result || []).filter((enrollment: Enrollment) => 
          enrollment.sessionTime && isScheduleInFuture(enrollment.sessionDate, enrollment.sessionTime.split(' - ')[0])
        )
      }
      
      // Sort by session date (IST), then by session time
      const sortedEnrollments = filteredEnrollments.sort((a: Enrollment, b: Enrollment) => {
        const dateComparison = compareISTDates(a.sessionDate, b.sessionDate)
        if (dateComparison === 0) {
          const timeA = a.sessionTime ? a.sessionTime.split(' - ')[0] : '00:00'
          const timeB = b.sessionTime ? b.sessionTime.split(' - ')[0] : '00:00'
          return compareTimesIST(timeA, timeB)
        }
        return dateComparison
      })
      
      setEnrollments(sortedEnrollments)
    } catch (error) {
      // Handle error silently
      toast.error('Failed to load enrollments')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStatusChange = (enrollment: Enrollment, newStatus: string) => {
    setConfirmationDialog({
      isOpen: true,
      type: 'status',
      enrollment,
      newStatus
    })
  }

  const updateEnrollmentStatus = async (enrollmentId: string, status: 'pending' | 'confirmed' | 'cancelled') => {
    try {
      setIsLoading(true) // Show loading state during update
      await apiService.updateEnrollmentStatus(enrollmentId, status)

      toast.success('Enrollment status updated successfully')
      await loadEnrollments()
      await loadSchedules() // Reload schedules to update capacity counts
    } catch (error) {
      // Handle error silently
      toast.error(error instanceof Error ? error.message : 'Failed to update enrollment status')
    } finally {
      setIsLoading(false) // Hide loading state
    }
  }

  const handleDeleteEnrollment = (enrollment: Enrollment) => {
    setConfirmationDialog({
      isOpen: true,
      type: 'delete',
      enrollment
    })
  }

  const deleteEnrollment = async (enrollmentId: string) => {
    try {
      setIsLoading(true) // Show loading state during deletion
      await apiService.deleteEnrollment(enrollmentId)

      toast.success('Enrollment deleted successfully')
      await loadEnrollments()
      await loadSchedules() // Reload schedules to update capacity counts
    } catch (error) {
      // Handle error silently
      toast.error(error instanceof Error ? error.message : 'Failed to delete enrollment')
    } finally {
      setIsLoading(false) // Hide loading state
    }
  }

  const handleConfirmAction = async () => {
    if (confirmationDialog.type === 'status' && confirmationDialog.enrollment && confirmationDialog.newStatus) {
      await updateEnrollmentStatus(confirmationDialog.enrollment.id, confirmationDialog.newStatus as 'pending' | 'confirmed' | 'cancelled')
    } else if (confirmationDialog.type === 'delete' && confirmationDialog.enrollment) {
      await deleteEnrollment(confirmationDialog.enrollment.id)
    } else if (confirmationDialog.type === 'edit' && confirmationDialog.schedule) {
      handleEditConfirm(confirmationDialog.schedule)
    } else if (confirmationDialog.type === 'delete' && confirmationDialog.schedule) {
      await deleteSchedule(confirmationDialog.schedule.id)
    }

    setConfirmationDialog({
      isOpen: false,
      type: null,
      enrollment: null,
      schedule: null
    })
  }

  const handleCancelAction = () => {
    setConfirmationDialog({
      isOpen: false,
      type: null,
      enrollment: null,
      schedule: null
    })
  }

  const onSubmit = async (data: ScheduleForm) => {
    setIsSubmitting(true)
    try {
      const selectedClass = classes.find(c => c.id === data.classId)
      if (!selectedClass) {
        toast.error('Selected class not found')
        return
      }

      const scheduleData = {
        classId: data.classId,
        className: selectedClass.name,
        specificDate: data.specificDate,
        startTime: data.startTime,
        endTime: data.endTime,
        isDemo: data.isDemo,
        maxCapacity: data.maxCapacity
      }

      if (editingSchedule) {
        await apiService.updateSchedule(editingSchedule.id, scheduleData)
      } else {
        await apiService.createSchedule(scheduleData)
      }

      toast.success(editingSchedule ? 'Schedule updated successfully' : 'Schedule created successfully')
      
      // Reload both schedules and enrollments
      await loadSchedules()
      await loadEnrollments()
      
      reset()
      setShowForm(false)
      setEditingSchedule(null)
    } catch (error) {
      // Handle error silently
      toast.error(error instanceof Error ? error.message : 'Failed to save schedule')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEdit = (schedule: Schedule) => {
    setConfirmationDialog({
      isOpen: true,
      type: 'edit',
      schedule
    })
  }

  const handleEditConfirm = (schedule: Schedule) => {
    setEditingSchedule(schedule)
    setValue('classId', schedule.classId)
    setValue('specificDate', schedule.specificDate)
    setValue('startTime', schedule.startTime)
    setValue('endTime', schedule.endTime)
    setValue('isDemo', schedule.isDemo)
    setValue('maxCapacity', schedule.maxCapacity)
    setShowForm(true)
  }

  const handleDelete = (schedule: Schedule) => {
    setConfirmationDialog({
      isOpen: true,
      type: 'delete',
      schedule
    })
  }

  const deleteSchedule = async (id: number) => {
    try {
      await apiService.deleteSchedule(id)

      toast.success('Schedule deleted successfully')
      
      // Reload both schedules and enrollments
      await loadSchedules()
      await loadEnrollments()
    } catch (error) {
      // Handle error silently
      toast.error(error instanceof Error ? error.message : 'Failed to delete schedule')
    }
  }

  const handleCancel = () => {
    reset()
    setShowForm(false)
    setEditingSchedule(null)
  }

  // Removed getDayName function as we only use specific dates now

  const getClassName = (classId: number) => {
    const danceClass = classes.find(c => c.id === classId)
    return danceClass ? danceClass.name : `Class ${classId}`
  }

  // Show loading while authentication is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
              <p className="text-gray-600 dark:text-gray-300 mt-1">Simplified Dance Studio Management</p>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Future Schedules</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{schedules.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Future Enrollments</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{enrollments.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                <Users className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Demo Enrollments</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {enrollments.filter(e => e.isDemo).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Toggle */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Data Filter</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {showAllData ? 'Showing all schedules and enrollments' : 'Showing only future schedules and enrollments'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">Future Only</span>
              <button
                onClick={() => setShowAllData(!showAllData)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showAllData ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showAllData ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">All Data</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('schedules')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'schedules'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Schedules ({schedules.length})
                  {isRefreshing && activeTab === 'schedules' && (
                    <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab('enrollments')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'enrollments'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Enrollments ({enrollments.length})
                  {isRefreshing && activeTab === 'enrollments' && (
                    <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'schedules' && (
          <>
            {/* Schedules List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Dance Schedules</h2>
                  {isRefreshing && (
                    <div className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400">
                      <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>Updating...</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {showAllData ? 'Showing all schedules' : 'Showing future schedules only (today and later)'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={refreshCurrentTab}
                  disabled={isRefreshing}
                  className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
                  title="Refresh schedules data"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  onClick={() => setShowForm(true)}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Schedule
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Class</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Capacity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {schedules.map((schedule) => (
                    <tr key={schedule.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {getClassName(schedule.classId)}
                        </div>
                        {schedule.isDemo && (
                          <div className="text-xs text-blue-600 dark:text-blue-400">Demo Session</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {(() => {
                          const { day, suffix, month, year } = formatReadableDateParts(schedule.specificDate)
                          return (
                            <>
                              {day}<sup className="text-xs">{suffix}</sup> {month} {year}
                            </>
                          )
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatTimeRangeWithAMPM(schedule.startTime, schedule.endTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <div className="flex flex-col">
                          <span className={schedule.isFullyBooked ? 'text-red-600 font-semibold' : 'text-gray-900 dark:text-white'}>
                            {schedule.isFullyBooked 
                              ? 'Fully Booked' 
                              : `${schedule.remainingSpots || 0}/${schedule.maxCapacity} spots`
                            }
                          </span>
                          {!schedule.isFullyBooked && (
                            <span className="text-xs text-gray-500">
                              {schedule.currentEnrollment || 0} enrolled
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(schedule)}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(schedule)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
          </>
        )}

        {activeTab === 'enrollments' && (
          <>
            {/* Enrollments List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Current Enrollments</h2>
                      {isRefreshing && (
                        <div className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400">
                          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span>Updating...</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {showAllData ? 'Showing all enrollments' : 'Showing enrollments for future sessions only'}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Total: {enrollments.length}
                    </div>
                    <button
                      onClick={refreshCurrentTab}
                      disabled={isRefreshing}
                      className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
                      title="Refresh enrollments data"
                    >
                      <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                    <button
                      onClick={() => loadEnrollments()}
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                {enrollments.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No enrollments yet</h3>
                    <p className="text-gray-500 dark:text-gray-400">Enrollments will appear here when students book classes.</p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Student
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Class
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Date & Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {enrollments.map((enrollment) => (
                        <tr key={enrollment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {enrollment.studentName}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {enrollment.studentEmail}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {enrollment.studentPhone}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {enrollment.className}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {(() => {
                                const { day, suffix, month, year } = formatReadableDateParts(enrollment.sessionDate)
                                return (
                                  <>
                                    {day}<sup className="text-xs">{suffix}</sup> {month} {year}
                                  </>
                                )
                              })()}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {enrollment.sessionTime ? (() => {
                                const [startTime, endTime] = enrollment.sessionTime.split(' - ')
                                return formatTimeRangeWithAMPM(startTime, endTime)
                              })() : 'Time not available'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              enrollment.isDemo 
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                                : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            }`}>
                              {enrollment.isDemo ? 'Demo' : 'Regular'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              value={enrollment.status}
                              onChange={(e) => handleStatusChange(enrollment, e.target.value)}
                              className={`text-xs font-semibold rounded-full px-2 py-1 border-0 ${
                                enrollment.status === 'confirmed'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                  : enrollment.status === 'cancelled'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                              }`}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleDeleteEnrollment(enrollment)}
                              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {/* Schedule Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}
                </h3>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Class
                  </label>
                  <select
                    {...register('classId', { valueAsNumber: true })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select a class</option>
                    {classes.map((danceClass) => (
                      <option key={danceClass.id} value={danceClass.id}>
                        {danceClass.name}
                      </option>
                    ))}
                  </select>
                  {errors.classId && (
                    <p className="text-sm text-red-600 mt-1">{errors.classId.message}</p>
                  )}
                </div>

                {/* Schedule type is always specific_date now */}

                {/* Always show specific date field since we only support specific dates */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Specific Date
                    </label>
                    <input
                      type="date"
                      {...register('specificDate')}
                      min={getMinDateIST()}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Only future dates are allowed (today and later)
                    </p>
                    {errors.specificDate && (
                      <p className="text-sm text-red-600 mt-1">{errors.specificDate.message}</p>
                    )}
                  </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      {...register('startTime')}
                      min={watch('specificDate') === getMinDateIST() ? getMinTimeIST() : '00:00'}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {watch('specificDate') === getMinDateIST() 
                        ? 'For today, only future times are allowed' 
                        : 'Any time is allowed for future dates'
                      }
                    </p>
                    {errors.startTime && (
                      <p className="text-sm text-red-600 mt-1">
                        {typeof errors.startTime.message === 'string' ? errors.startTime.message : 'Invalid start time'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      {...register('endTime')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    {errors.endTime && (
                      <p className="text-sm text-red-600 mt-1">
                        {typeof errors.endTime.message === 'string' ? errors.endTime.message : 'Invalid end time'}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Capacity
                  </label>
                  <input
                    type="number"
                    {...register('maxCapacity', { valueAsNumber: true })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {errors.maxCapacity && (
                    <p className="text-sm text-red-600 mt-1">{errors.maxCapacity.message}</p>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('isDemo')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                    Demo Session
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        {editingSchedule ? 'Update' : 'Create'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {confirmationDialog.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {confirmationDialog.type === 'status' ? 'Confirm Status Change' : 
                 confirmationDialog.type === 'edit' ? 'Confirm Edit' : 'Confirm Deletion'}
              </h3>
              
              {confirmationDialog.enrollment && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <strong>Student:</strong> {confirmationDialog.enrollment.studentName}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <strong>Class:</strong> {confirmationDialog.enrollment.className}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <strong>Date:</strong> {(() => {
                      const { day, suffix, month, year } = formatReadableDateParts(confirmationDialog.enrollment.sessionDate)
                      return (
                        <>
                          {day}<sup className="text-xs">{suffix}</sup> {month} {year}
                        </>
                      )
                    })()}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <strong>Time:</strong> {confirmationDialog.enrollment.sessionTime ? (() => {
                      const [startTime, endTime] = confirmationDialog.enrollment.sessionTime.split(' - ')
                      return formatTimeRangeWithAMPM(startTime, endTime)
                    })() : 'Time not available'}
                  </p>
                  {confirmationDialog.type === 'status' && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      <strong>New Status:</strong> 
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                        confirmationDialog.newStatus === 'confirmed'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                          : confirmationDialog.newStatus === 'cancelled'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                      }`}>
                        {confirmationDialog.newStatus ? confirmationDialog.newStatus.charAt(0).toUpperCase() + confirmationDialog.newStatus.slice(1) : ''}
                      </span>
                    </p>
                  )}
                  {confirmationDialog.type === 'status' && confirmationDialog.newStatus === 'confirmed' && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 mb-2">
                      ⚠️ A confirmation email will be sent to the student.
                    </p>
                  )}
                </div>
              )}

              {confirmationDialog.schedule && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <strong>Class:</strong> {getClassName(confirmationDialog.schedule.classId)}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <strong>Date:</strong> {(() => {
                      const { day, suffix, month, year } = formatReadableDateParts(confirmationDialog.schedule.specificDate)
                      return (
                        <>
                          {day}<sup className="text-xs">{suffix}</sup> {month} {year}
                        </>
                      )
                    })()}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <strong>Time:</strong> {formatTimeRangeWithAMPM(confirmationDialog.schedule.startTime, confirmationDialog.schedule.endTime)}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <strong>Type:</strong> 
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                      confirmationDialog.schedule.isDemo
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                    }`}>
                      {confirmationDialog.schedule.isDemo ? 'Demo' : 'Regular'}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <strong>Capacity:</strong> {confirmationDialog.schedule.isFullyBooked 
                      ? `Fully Booked (${confirmationDialog.schedule.maxCapacity} max)` 
                      : `${confirmationDialog.schedule.remainingSpots || 0}/${confirmationDialog.schedule.maxCapacity} spots available`
                    }
                  </p>
                  {!confirmationDialog.schedule.isFullyBooked && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      <strong>Enrolled:</strong> {confirmationDialog.schedule.currentEnrollment || 0} students
                    </p>
                  )}
                  {confirmationDialog.type === 'edit' && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 mb-2">
                      ⚠️ This will open the edit form for this schedule.
                    </p>
                  )}
                </div>
              )}

              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                {confirmationDialog.type === 'status' 
                  ? 'Are you sure you want to change the enrollment status?'
                  : confirmationDialog.type === 'edit'
                  ? 'Are you sure you want to edit this schedule?'
                  : confirmationDialog.type === 'delete' && confirmationDialog.enrollment
                  ? 'Are you sure you want to delete this enrollment? This action cannot be undone.'
                  : 'Are you sure you want to delete this schedule? This action cannot be undone.'
                }
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={handleCancelAction}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmAction}
                  className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                    confirmationDialog.type === 'delete'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {confirmationDialog.type === 'status' ? 'Confirm Change' : 
                   confirmationDialog.type === 'edit' ? 'Edit' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}